/**
 * DOM-side answer to "is this candidate badge rect actually clear?" — the
 * `IsFree` predicate handed to `computeBadgePlacement` (`./badgePlacement.ts`).
 *
 * Ported 2026-10-16 from the social SPA. For the EXACT rect a candidate would
 * occupy, ~5 points (the 4 corners inset 2px + the centre) are sampled with
 * `document.elementsFromPoint`; the rect is occupied if ANY element in ANY
 * sampled point's full z-stack is:
 *   - interactive (`interactiveSelector`),
 *   - media (`mediaSelector`), or
 *   - carrying its OWN text whose actual glyph-bearing line box (via
 *     `Range.getClientRects()`, not just the element's bounding box) contains
 *     that point.
 * A plain layout `<div>` with none of the above is never an obstacle on its own
 * — `elementsFromPoint` returns the whole stack so a wrapper doesn't hide the
 * control underneath. Elements matching `ignoreSelector` (the badges themselves
 * and the popover) are never obstacles, nor is anything inside one.
 *
 * The selector sets are options; the exported defaults are social's.
 */

export const DEFAULT_INTERACTIVE_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="link"], [role="option"], [role="tab"], [role="checkbox"], [role="switch"], [role="combobox"], [tabindex]:not([tabindex="-1"])';

export const DEFAULT_MEDIA_SELECTOR = "img, svg, canvas, video";

/** Never an obstacle: this package's own badges / layer / popover, plus social's
 * legacy badge attribute so a migrating app keeps working unchanged. */
export const DEFAULT_IGNORE_SELECTOR =
  '[data-learn-badge], [data-plerity-learn-badge], [data-learn-overlay], [data-plerity-learn-tip-badge], [role="tooltip"]';

/** How far inside each edge of the candidate rect to sample, so a point doesn't
 * land exactly ON a shared border between two adjacent elements. */
const DEFAULT_SAMPLE_INSET = 2;

export interface ObstacleSelectorOptions {
  /** Elements that count as obstacles when hit. Default: `DEFAULT_INTERACTIVE_SELECTOR`. */
  interactiveSelector?: string;
  /** Media elements that count as obstacles when hit. Default: `DEFAULT_MEDIA_SELECTOR`. */
  mediaSelector?: string;
  /** Elements (and their descendants) never counted as obstacles. Default: `DEFAULT_IGNORE_SELECTOR`. */
  ignoreSelector?: string;
  /** Px inset of the sample points from the rect edge. Default 2. */
  sampleInset?: number;
}

interface SampleRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/** True when `(x, y)` falls inside one of `el`'s own DIRECT text nodes' actual
 * rendered line boxes. */
function ownTextContainsPoint(el: Element, x: number, y: number): boolean {
  for (const node of el.childNodes) {
    if (node.nodeType !== Node.TEXT_NODE) continue;
    if (!(node.textContent ?? "").trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) {
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return true;
    }
  }
  return false;
}

function samplePoints(rect: SampleRect, inset: number): Array<[number, number]> {
  const left = rect.left + inset;
  const right = rect.right - inset;
  const top = rect.top + inset;
  const bottom = rect.bottom - inset;
  const cx = (rect.left + rect.right) / 2;
  const cy = (rect.top + rect.bottom) / 2;
  return [
    [left, top],
    [right, top],
    [left, bottom],
    [right, bottom],
    [cx, cy],
  ];
}

/**
 * Builds an `IsFree` predicate bound to the given selector set. Safe to call for
 * a rect partially or fully outside the viewport — `elementsFromPoint` returns
 * an empty stack there, treated as "free" (the caller separately rejects
 * out-of-viewport candidates). Fails open when `elementsFromPoint` is absent.
 */
export function createRectFreeChecker(options: ObstacleSelectorOptions = {}): (rect: SampleRect) => boolean {
  const interactive = options.interactiveSelector ?? DEFAULT_INTERACTIVE_SELECTOR;
  const media = options.mediaSelector ?? DEFAULT_MEDIA_SELECTOR;
  const ignore = options.ignoreSelector ?? DEFAULT_IGNORE_SELECTOR;
  const inset = options.sampleInset ?? DEFAULT_SAMPLE_INSET;

  const isObstacleHit = (el: Element, x: number, y: number): boolean => {
    if (ignore && el.closest(ignore)) return false;
    if (interactive && el.matches(interactive)) return true;
    if (media && el.matches(media)) return true;
    return ownTextContainsPoint(el, x, y);
  };

  return (rect) => {
    if (typeof document.elementsFromPoint !== "function") return true;
    for (const [x, y] of samplePoints(rect, inset)) {
      for (const el of document.elementsFromPoint(x, y)) {
        if (isObstacleHit(el, x, y)) return false;
      }
    }
    return true;
  };
}

/** `isRectFree` with the default (social) selector set. */
export const isRectFree = createRectFreeChecker();
